<template>
  <div class="add-payment-method-container">
    <!-- 載入中狀態 -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>正在處理中...</p>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h2>授權失敗</h2>
      <p>{{ error }}</p>
      <button @click="closeIframe" class="btn btn-secondary">關閉</button>
    </div>

    <!-- In-App Browser 警告 -->
    <div v-else-if="isInAppBrowser" class="warning-state">
      <div class="warning-icon">🔒</div>
      <h2>請使用外部瀏覽器</h2>
      <p>由於 LINE/Facebook 內建瀏覽器的限制，請點擊右上角選單，選擇「在瀏覽器中開啟」。</p>
      <button @click="copyCurrentUrl" class="btn btn-primary">複製當前網址</button>
      <button @click="closeIframe" class="btn btn-secondary">取消</button>
    </div>

    <!-- 表單容器（藍新授權表單會插入這裡） -->
    <div v-else ref="formContainer" class="form-container"></div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { isTrustedGhlMessage, postToGhl, postToGhlSingle } from '../utils/ghlMessaging';
import {
  buildProviderReadyPayload,
  buildSuccessResponsePayload,
  buildErrorResponsePayload,
  buildCloseResponsePayload,
  parseSetupInitiateProps,
} from '../utils/ghlEventPayloads';

export default {
  name: 'AddPaymentMethod',
  setup() {
    const isLoading = ref(true);
    const error = ref(null);
    const isInAppBrowser = ref(false);
    const formContainer = ref(null);
    
    let ghlProps = null; // 儲存 GHL 傳入的 props
    let messageHandler = null; // 事件監聽器

    /**
     * 偵測是否在 In-App Browser 中（LINE/Facebook）
     * 參考：docs/payment-popup-solution.md
     */
    const detectInAppBrowser = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      
      // LINE In-App Browser
      if (ua.includes('Line/')) {
        return true;
      }
      
      // Facebook In-App Browser (FBAN/FBAV)
      if (ua.includes('FBAN') || ua.includes('FBAV')) {
        return true;
      }
      
      // Instagram In-App Browser
      if (ua.includes('Instagram')) {
        return true;
      }
      
      return false;
    };

    /**
     * 複製當前 URL 到剪貼簿
     */
    const copyCurrentUrl = async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('網址已複製，請貼到外部瀏覽器中開啟');
      } catch (err) {
        console.error('複製失敗:', err);
        // Fallback: 顯示網址供使用者手動複製
        prompt('請複製此網址:', window.location.href);
      }
    };

    /**
     * 關閉 GHL IFrame（發送 close 事件）
     */
    const closeIframe = () => {
      if (window.parent !== window) {
        postToGhl(window.parent, buildCloseResponsePayload());
      }
    };

    /**
     * 發送成功事件給 GHL（新增付款方式：官方 §9.1 為裸事件，不含 chargeId/data）
     */
    const sendSuccessEvent = () => {
      if (window.parent !== window) {
        postToGhlSingle(window.parent, buildSuccessResponsePayload());
      }
    };

    /**
     * 發送錯誤事件給 GHL
     * @param {string} message - 錯誤訊息
     */
    const sendErrorEvent = (message) => {
      if (window.parent !== window) {
        postToGhlSingle(window.parent, buildErrorResponsePayload({ description: message }));
      }
    };

    /**
     * 產生授權表單並提交到藍新金流
     */
    const generateAndSubmitForm = async (props) => {
      try {
        isLoading.value = true;
        error.value = null;

        const { locationId, contactId, publishableKey, contactName } = parseSetupInitiateProps(props);
        if (!locationId || !contactId) throw new Error('缺少必要參數（locationId、contactId）');

        const contextResponse = await axios.post('/api/payment-method/context', {
          locationId,
          contactId,
          publishableKey,
          contactName,
        });
        const contextToken = contextResponse.data?.contextToken;
        if (!contextToken) throw new Error('無法建立付款上下文');

        const response = await axios.post('/api/payment-method/authorize', { contextToken });

        if (!response.data.success) {
          throw new Error(response.data.message || '產生授權表單失敗');
        }

        const { formHtml } = response.data;

        // 將表單插入 DOM
        if (formContainer.value) {
          formContainer.value.innerHTML = formHtml;

          // 自動提交表單（藍新 SDK 產生的表單會包含 auto-submit script）
          // 如果沒有自動提交，手動觸發
          setTimeout(() => {
            const form = formContainer.value.querySelector('form');
            if (form) {
              form.submit();
            }
          }, 100);
        }

        isLoading.value = false;
      } catch (err) {
        console.error('[AddPaymentMethod] 產生表單失敗:', err);
        error.value = err.response?.data?.message || err.message || '產生授權表單失敗';
        isLoading.value = false;
        
        // 發送錯誤事件給 GHL
        sendErrorEvent(error.value);
      }
    };

    /**
     * 處理 GHL IFrame 訊息事件
     */
    messageHandler = (event) => {
      if (!isTrustedGhlMessage(event) || !event.data || !event.data.type) {
        return;
      }

      console.log('[AddPaymentMethod] 接收 GHL 事件:', event.data);

      switch (event.data.type) {
        case 'setup_initiate_props':
          // GHL 傳入初始化參數
          ghlProps = event.data.data || event.data;
          console.log('[AddPaymentMethod] 接收 GHL props:', ghlProps);
          
          // 產生並提交授權表單
          generateAndSubmitForm(ghlProps);
          break;

        case 'authorization_success':
          // 授權成功（從 PaymentMethodReturn.vue 接收）
          sendSuccessEvent();
          break;

        case 'authorization_failed':
          // 授權失敗（從 PaymentMethodReturn.vue 接收）
          error.value = event.data.data.message;
          sendErrorEvent(error.value);
          break;

        default:
          console.warn('[AddPaymentMethod] 未知事件類型:', event.data.type);
      }
    };

    onMounted(() => {
      console.log('[AddPaymentMethod] 元件已掛載');

      // 1. 偵測 In-App Browser
      isInAppBrowser.value = detectInAppBrowser();
      if (isInAppBrowser.value) {
        console.warn('[AddPaymentMethod] 偵測到 In-App Browser');
        isLoading.value = false;
        return; // 不繼續處理
      }

      // 2. 註冊訊息監聽器
      window.addEventListener('message', messageHandler);

      // 3. 通知 GHL IFrame 已就緒
      if (window.parent !== window) {
        postToGhlSingle(window.parent, buildProviderReadyPayload({ addCardOnFileSupported: true }));
        console.log('[AddPaymentMethod] 已發送 custom_provider_ready 事件');
      }

      isLoading.value = false;
    });

    onBeforeUnmount(() => {
      // 移除事件監聽器
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
      }
    });

    return {
      isLoading,
      error,
      isInAppBrowser,
      formContainer,
      copyCurrentUrl,
      closeIframe,
    };
  },
};
</script>

<style scoped>
.add-payment-method-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #f5f5f5;
}

.loading-state,
.error-state,
.warning-state {
  text-align: center;
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon,
.warning-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-state h2 {
  color: #e74c3c;
  margin-bottom: 12px;
}

.warning-state h2 {
  color: #f39c12;
  margin-bottom: 12px;
}

.error-state p,
.warning-state p {
  color: #555;
  margin-bottom: 24px;
  line-height: 1.6;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s;
  margin: 0 8px;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover {
  background: #2980b9;
}

.btn-secondary {
  background: #95a5a6;
  color: white;
}

.btn-secondary:hover {
  background: #7f8c8d;
}

.form-container {
  width: 100%;
  max-width: 600px;
}
</style>
